import { DateTime } from "luxon";
import { autovioColors } from "../misc/backofficeTheme";
import {
  AutovioCalendarEvent,
  isCourseLesson,
  isPaidLesson,
  OtherEventDuration,
  TheoryLesson,
} from "../model/autovioCalendarEvents";
import { TinyColor } from "@ctrl/tinycolor";
import { assertNever, replaceCommasWithDot, reportError } from "../backoffice.utils";
import type { EventContentArg, EventInput, FormatterInput } from "@fullcalendar/core";
import { DateWithoutTime } from "../model/DateWithoutTime";
import { drivingSchoolsProvider } from "../providers/drivingSchoolsProvider";
import { l10n } from "../l10n";

export const calendarSlotLabelFormat: FormatterInput = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

export function calendarEventTitle(event: AutovioCalendarEvent, options: { omitPrefix?: boolean } = {}): string {
  const { omitPrefix } = options;
  const title = _calendarEventTitle(event);
  if (wasCancelled(event) && !omitPrefix) {
    return `Abgesagt: ${title}`;
  }
  if (isPaidLesson(event) && event.student.rsvp === "no-show" && !omitPrefix) {
    return `Nicht erschienen: ${title}`;
  }
  return title;
}

function _calendarEventTitle(event: AutovioCalendarEvent): string {
  switch (event.type) {
    case "DrivingLesson":
      switch (event.drivingLessonType) {
        case "normal":
          return "Übungsstunde";
        case "unterweisungAmFahrzeug":
          return "Unterweisung am Fahrzeug";
        case "praktischeUnterweisung":
          return "Praktische Unterweisung";
        case "ueberlandfahrt":
          return "Überlandfahrt";
        case "autobahnfahrt":
          return "Autobahnfahrt";
        case "nachtfahrt":
          return "Nachtfahrt";
        case "simulator":
          return "Simulator-Fahrstunde";
        case "praktischePruefung":
          return "Praktische Prüfung";
        case "schaltkompetenz":
          return "Schaltkompetenz";
        case "fahrprobe":
          return "ASF Fahrprobe";
        default:
          return `[${event.drivingLessonType}]`;
      }
    case "TheoryLesson":
      return "Theorieunterricht";
    case "TheoryExam":
      return "Theorieprüfung";
    case "RemoteLesson":
      return "Online Meeting";
    case "ASFCourseSession":
      switch (event.sessionType) {
        case "sitzung1":
          return "ASF Kurs Sitzung 1";
        case "fahrprobe":
          return "ASF Kurs Fahrprobe (Blocker)";
        case "sitzung2":
          return "ASF Kurs Sitzung 2";
        case "sitzung3":
          return "ASF Kurs Sitzung 3";
        case "sitzung4":
          return "ASF Kurs Sitzung 4";
        default:
          return `[${event.sessionType}]`;
      }
    case "CourseLesson":
      return event.courseModuleName;
    case "Other": {
      const drivingSchool = drivingSchoolsProvider.getOneFromCache(event.drivingSchoolId);
      const otherEventCategory = drivingSchool?.otherCalendarEventCategories[event.categoryId];
      if (!otherEventCategory?.name) {
        reportError(
          `Other Event ${event.id} has no category name (categoryId: ${event.categoryId}, drivingSchoolId: ${event.drivingSchoolId}) -- using fallback "Sonstiges"`,
        );
      }
      return otherEventCategory?.name || "Sonstiges";
    }
    case "VehicleEvent":
      return l10n.vehicleEventType[event.vehicleEventType] || "Fahrzeugtermin";
    case "Unknown":
      return "Fehler beim Anzeigen";
  }
}

export function calendarEventBackgroundColor(event: AutovioCalendarEvent): string {
  const color = calendarEventColor(event);
  if (wasCancelled(event) || (isPaidLesson(event) && event.student.rsvp === "no-show")) {
    return _withOpacity(color, 0.5); // ... (APP-4566, APP-4596)
  } else if (isCourseLesson(event)) {
    if (!(event.lecturerId && event.roomId)) {
      return _withOpacity(color, 0.5);
    }
  }
  return color;
}

export function calendarEventColor(
  event:
    | {
        type: "DrivingLesson";
        drivingLessonType:
          | "normal"
          | "unterweisungAmFahrzeug"
          | "praktischeUnterweisung"
          | "ueberlandfahrt"
          | "autobahnfahrt"
          | "nachtfahrt"
          | "simulator"
          | "praktischePruefung"
          | "schaltkompetenz"
          | "theoretischePruefung"
          | "fahrprobe";
      }
    | { type: "ASFCourseSession"; sessionType: "sitzung1" | "fahrprobe" | "sitzung2" | "sitzung3" | "sitzung4" }
    | { type: "CourseLesson"; courseModuleColor: string }
    | { type: "TheoryLesson" | "TheoryExam" | "RemoteLesson" | "Other" | "VehicleEvent" | "Unknown" },
): string {
  switch (event.type) {
    case "DrivingLesson":
      switch (event.drivingLessonType) {
        case "normal":
        case "unterweisungAmFahrzeug":
        case "praktischeUnterweisung":
        case "fahrprobe":
          return autovioColors.green;
        case "ueberlandfahrt":
          return autovioColors.yellow;
        case "autobahnfahrt":
          return autovioColors.blue;
        case "nachtfahrt":
          return autovioColors.black;
        case "praktischePruefung":
        case "schaltkompetenz":
          return autovioColors.orange;
        case "theoretischePruefung":
          return autovioColors.orangeMediumDark;
        case "simulator":
          return autovioColors.greenMediumDark;
        default:
          console.warn(`Unknown driving lesson type ${event.drivingLessonType} -- using color: red`);
          return autovioColors.red;
      }
    case "TheoryLesson":
      return autovioColors.violet;
    case "TheoryExam":
      return autovioColors.orangeMediumDark;
    case "RemoteLesson":
      return autovioColors.greyUltraLight;
    case "ASFCourseSession":
      return event.sessionType === "fahrprobe" ? autovioColors.green : autovioColors.violet;
    case "CourseLesson":
      return event.courseModuleColor;
    case "Other":
    case "VehicleEvent":
      return autovioColors.grey;
    default:
      // Fallback for unknown events ...
      return autovioColors.red;
  }
}

export const calendarEventTextColor = (event: AutovioCalendarEvent): string => {
  if (event.type === "RemoteLesson") {
    return autovioColors.black;
  } else if (event.type === "CourseLesson") {
    const { courseModuleColor: backgroundColor } = event;
    if (backgroundColor.startsWith("#")) {
      return new TinyColor(backgroundColor).getBrightness() > 150 ? autovioColors.black : autovioColors.white;
    }
  }
  return autovioColors.white;
};

export const otherEventDurationLabels: Record<OtherEventDuration, string> = {
  halfDay: "halber Tag",
  fullDay: "ganzer Tag",
  halfDayInTheMorning: "halber Tag (am Morgen)",
  halfDayInTheAfternoon: "halber Tag (am Nachmittag)",
};

export const renderTheoryLessonSlots = (theoryLesson: TheoryLesson) => {
  const { numBookedStudents, maxStudents } = theoryLesson;
  return `${numBookedStudents}/${maxStudents}`;
};

/**
 * Format: 09:46
 */
export const formatTime = (date: DateTime) => {
  return `${date.toLocaleString(DateTime.TIME_SIMPLE, { locale: "de-DE" })}`;
};

export const formatDate = (input?: string | DateTime | DateWithoutTime, asYesterdayTodayOrTomorrow?: boolean) => {
  if (input) {
    const dateTime =
      typeof input === "string"
        ? DateTime.fromISO(input, { zone: "Europe/Berlin" })
        : input instanceof DateWithoutTime
          ? input.toDateTime()
          : input;
    if (
      asYesterdayTodayOrTomorrow &&
      Math.abs(DateTime.now().endOf("day").diff(dateTime.endOf("day"), "days").days) <= 1
    ) {
      return dateTime.toRelativeCalendar({ locale: "de-DE", unit: "days" });
    }
    return dateTime.toLocaleString(DateTime.DATE_SHORT, { locale: "de-DE" });
  }
  return "";
};

export const formatDateTime = (date: DateTime, asToday?: boolean) => {
  const today = DateTime.now().setLocale("de-DE").startOf("day");
  const isToday = date.hasSame(today, "day");

  if (isToday && asToday) {
    return replaceCommasWithDot(`Heute, ${date.toLocaleString(DateTime.TIME_SIMPLE, { locale: "de-DE" })} Uhr`);
  }

  return replaceCommasWithDot(`${date.toLocaleString(DateTime.DATETIME_MED, { locale: "de-DE" })} Uhr`);
};

export const formatDateOrTime = (date: DateTime, asToday?: boolean) => {
  const today = DateTime.now().setLocale("de-DE").startOf("day");
  const isToday = date.hasSame(today, "day");

  if (isToday && asToday) {
    return `${date.toLocaleString(DateTime.TIME_SIMPLE, { locale: "de-DE" })} Uhr`;
  }

  return formatDate(date);
};

/**
 * Format: 12.10.2021 • 09:00 - 10:00 or Heute • 09:00 - 10:00
 * */
export const formatDateTimeRange = (dateTime: DateTime, end: DateTime) => {
  // if the date is yesterday, today or tomorrow, we want to display "Gestern", "Heute" or "Morgen" instead of the date
  let startDate =
    Math.abs(DateTime.now().endOf("day").diff(dateTime.endOf("day"), "days").days) <= 1
      ? dateTime.toRelativeCalendar({
          locale: "de-DE",
          unit: "days",
        })
      : dateTime.toLocaleString(DateTime.DATE_SHORT, { locale: "de-DE" });
  startDate = startDate ?? dateTime.toLocaleString(DateTime.DATE_SHORT, { locale: "de-DE" });

  const capitalizedStartDate = startDate.charAt(0).toUpperCase() + startDate.slice(1);

  return `${capitalizedStartDate} • ${dateTime.toLocaleString(DateTime.TIME_SIMPLE, {
    locale: "de-DE",
  })} - ${end.toLocaleString(DateTime.TIME_SIMPLE, { locale: "de-DE" })}`;
};

export function formatCalendarWeek(date: DateTime, now?: DateTime): string {
  if (!date.isValid) {
    throw new Error(`Invalid date: ${date.invalidReason}`);
  }
  let result = `KW ${date.weekNumber}`;
  let year = date.get("year");
  // Adjust the displayed year if the date lies in january but still
  // belongs to the calendar week of the previous year to avoid confusion.
  // Example: 2023-01-01 will be formatted to "KW 52, 2022".
  if (date.weekNumber >= 52 && date.get("month") === 1) {
    year -= 1;
  }
  if (year !== (now ?? DateTime.now()).get("year")) {
    result += `, ${year}`;
  }
  return result;
}

export function wasCancelled(event: AutovioCalendarEvent): boolean {
  // Cancelled by instructor?
  if (event.deleted) {
    return true;
  }
  // Cancelled by student?
  return (
    isPaidLesson(event) &&
    event.student.rsvp !== "pending" &&
    event.student.rsvp !== "accepted" &&
    event.student.rsvp !== "no-show"
  );
}

/**
 * Use this function as the eventClassNames property of a `FullCalendar` element.
 */
export function eventClassNames(arg: EventContentArg) {
  const classNames: string[] = [];
  classNames.push("overflow-hidden");
  const event = arg.event.extendedProps?.event as AutovioCalendarEvent | undefined;
  if (!event) {
    return classNames;
  }
  if (event.student?.rsvp === "pending") {
    classNames.push("pending-event");
  }
  if (wasCancelled(event)) {
    classNames.push("canceled-event");
  }
  return classNames;
}

export function durationInMinutes({ start, end }: { start: DateTime; end: DateTime }): number {
  return end.diff(start).as("minutes");
}

export interface PublicHoliday {
  name: string;
  date: DateWithoutTime;
}

export function publicHolidayToEventInput(publicHoliday: PublicHoliday): EventInput {
  return {
    title: "Feiertag",
    id: publicHoliday.date.toString(),
    backgroundColor: autovioColors.grey,
    color: autovioColors.grey,
    textColor: autovioColors.white,
    editable: false,
    start: publicHoliday.date.at(6).toISO(),
    end: publicHoliday.date.nextDay.at(0).toISO(),
    extendedProps: {
      publicHoliday,
      extraDetails: {
        subtitle: publicHoliday.name,
      },
    },
  };
}

export function autovioCalendarEventToEventInput(
  event: AutovioCalendarEvent,
  { warnings }: { warnings?: Array<string> } = {},
): Defined<EventInput, "extendedProps"> {
  return {
    id: event.id,
    title: calendarEventTitle(event),
    backgroundColor: warnings?.length ? autovioColors.redUltraLight : calendarEventBackgroundColor(event),
    textColor: warnings?.length ? autovioColors.red : calendarEventTextColor(event),
    editable: false,
    start: event.start.toISO(),
    end: event.end.toISO(),
    extendedProps: { event, warnings },
  };
}

function _withOpacity(color: string, opacity: 0.5): string {
  if (!color.startsWith("#") || color.length !== 7) {
    throw new Error(`Unexpected color: ${JSON.stringify(color)} -- don't know how to apply opacity`);
  }
  if (opacity === 0.5) {
    return `${color}7f`;
  } else {
    assertNever(opacity);
  }
}
